import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { 
  collection, query, where, onSnapshot, doc, 
  updateDoc, serverTimestamp, Timestamp 
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { 
  IoCheckboxOutline, IoSquareOutline, IoCheckmarkCircleOutline, IoConstructOutline 
} from 'react-icons/io5';
import './TrabajadorDashboardScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';

function TrabajadorActividadesScreen() {
  const { currentUser, userData } = useAuth();
  
  const [actividades, setActividades] = useState([]);
  const [reportesAsignados, setReportesAsignados] = useState([]);

  useEffect(() => {
    if (!currentUser || !userData?.condominioId) return;

    // 1. Actividades diarias
    const qActividades = query(
      collection(db, "actividades_diarias"),
      where("condominioId", "==", userData.condominioId)
    );
    const unsubActividades = onSnapshot(qActividades, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setActividades(list);
    });

    // 2. Reportes asignados
    const qReportes = query(
      collection(db, "reportes"),
      where("condominioId", "==", userData.condominioId),
      where("asignadoA", "==", currentUser.uid)
    );
    const unsubReportes = onSnapshot(qReportes, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setReportesAsignados(list);
    });

    return () => {
      unsubActividades();
      unsubReportes();
    };
  }, [currentUser, userData]);

  const handleToggleActividad = async (act) => {
    const hoyStr = new Date().toISOString().split('T')[0];
    const completedToday = act.completadoPor && act.completadoPor[hoyStr];
    
    const actRef = doc(db, "actividades_diarias", act.id);
    const updatedCompleted = act.completadoPor || {};

    if (completedToday) {
      delete updatedCompleted[hoyStr];
    } else {
      updatedCompleted[hoyStr] = {
        completado: true,
        fecha: Timestamp.now(),
        usuarioId: currentUser.uid
      };
    }

    try {
      await updateDoc(actRef, { completadoPor: updatedCompleted });
    } catch (error) {
      console.error("Error toggling activity:", error);
    }
  };

  const handleResolverReporte = async (repId) => {
    if (!window.confirm("¿Confirmas que has resuelto esta tarea/incidencia asignada?")) return;
    try {
      await updateDoc(doc(db, "reportes", repId), {
        status: 'completado',
        resueltoPor: currentUser.uid,
        fechaResolucion: serverTimestamp(),
        comentariosResolucion: 'Resuelto por el trabajador operativo.'
      });
      alert("Reporte marcado como resuelto exitosamente.");
    } catch (error) {
      console.error("Error resolving report:", error);
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/trabajador/actividades" />

      <main className="ks-main">
        <SafeAreaInsets top={false} bottom={true} />
        <KondoBrandHeader showBack={false} />

        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Actividades</h1>
                <p>Lista de tareas y reportes</p>
              </div>
              <IoCheckboxOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div style={{ padding: '16px' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            
            {/* Actividades Diarias */}
            <div className="trabajador-card-premium" style={{ marginBottom: '20px' }}>
              <div className="card-section-title">
                <IoCheckboxOutline size={22} style={{ color: '#7B61FF' }} />
                <h3 style={{ fontSize: '1.2rem' }}>Actividades Diarias</h3>
              </div>
              
              {actividades.length === 0 ? (
                <div className="dashboard-empty-state">
                  <p>No hay actividades programadas para hoy.</p>
                </div>
              ) : (
                <div className="checklist-items">
                  {actividades.map(act => {
                    const hoyStr = new Date().toISOString().split('T')[0];
                    const isChecked = act.completadoPor && act.completadoPor[hoyStr];
                    return (
                      <div key={act.id} className="checklist-row" onClick={() => handleToggleActividad(act)}>
                        {isChecked ? (
                          <IoCheckmarkCircleOutline size={22} style={{ color: '#22c55e' }} />
                        ) : (
                          <IoSquareOutline size={22} style={{ color: '#94a3b8' }} />
                        )}
                        <span className={isChecked ? 'completed' : ''}>{act.nombre}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reportes Asignados */}
            <div className="trabajador-card-premium">
              <div className="card-section-title">
                <IoConstructOutline size={22} style={{ color: '#7B61FF' }} />
                <h3 style={{ fontSize: '1.2rem' }}>Reportes Asignados</h3>
              </div>

              {reportesAsignados.length === 0 ? (
                <div className="dashboard-empty-state">
                  <p>No tienes reportes asignados pendientes.</p>
                </div>
              ) : (
                <div className="reports-assigned-list">
                  {reportesAsignados.map(rep => (
                    <div key={rep.id} className="assigned-report-card">
                      <div className="report-card-top">
                        <span className="report-unit-badge">Unidad {rep.unidadResidente}</span>
                        <span className={`report-status-badge ${rep.status}`}>{rep.status}</span>
                      </div>
                      <h4>{rep.titulo || 'Reporte de Mantenimiento'}</h4>
                      <p>{rep.descripcion}</p>
                      
                      {rep.status !== 'completado' && (
                        <button 
                          className="resolve-report-btn"
                          onClick={() => handleResolverReporte(rep.id)}
                        >
                          <IoCheckmarkCircleOutline size={16} />
                          <span>Marcar como Resuelto</span>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  </main>

      <KondoBottomNav activeRoute="/trabajador/actividades" />
    </div>
  );
}

export default TrabajadorActividadesScreen;
